import React from 'react';
import { Users, Search, Calendar, Filter, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { getSessionStatus } from './CompletedInterviews';

interface AllCandidatesListProps {
  allCandidates: any[];
  candidatesLoading: boolean;
  candidatesTotal: number;
  candidateSearch: string;
  setCandidateSearch: (value: string) => void;
  candidateStatusFilter: string;
  setCandidateStatusFilter: (value: string) => void;
  candidateDateFilter: { start: string; end: string };
  setCandidateDateFilter: (value: { start: string; end: string }) => void;
  currentPage?: number;
  totalPages?: number;
  onPageChange?: (page: number) => void;
}

const AllCandidatesList: React.FC<AllCandidatesListProps> = ({
  allCandidates,
  candidatesLoading,
  candidatesTotal,
  candidateSearch,
  setCandidateSearch,
  candidateStatusFilter,
  setCandidateStatusFilter,
  candidateDateFilter,
  setCandidateDateFilter,
  currentPage = 1,
  totalPages = 1,
  onPageChange,
}) => {
  return (
    <div className="p-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">All Candidates</h1>
          <p className="text-lg text-gray-600">
            View and manage all candidates across all assessments.
          </p>
        </div>

        {/* Filters Section */}
        <div className="bg-white rounded-lg border border-gray-200 p-4 md:p-6 mb-6 shadow-sm overflow-hidden">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-2">
            <h3 className="text-lg font-semibold text-gray-900">Filters</h3>
            {(candidateSearch || candidateStatusFilter !== 'all' || candidateDateFilter.start || candidateDateFilter.end) && (
              <button
                onClick={() => {
                  setCandidateSearch('');
                  setCandidateStatusFilter('all');
                  setCandidateDateFilter({ start: '', end: '' });
                }}
                className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center space-x-1 self-start sm:self-auto"
              >
                <X className="h-4 w-4" />
                <span>Clear All</span>
              </button>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Search */}
            <div className="md:col-span-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search candidates..."
                  value={candidateSearch}
                  onChange={(e) => setCandidateSearch(e.target.value)}
                  className="w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                />
              </div>
            </div>

            {/* Status Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
              <div className="relative">
                <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                <select
                  value={candidateStatusFilter}
                  onChange={(e) => setCandidateStatusFilter(e.target.value)}
                  className="w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white appearance-none cursor-pointer"
                >
                  <option value="all">All Status</option>
                  <option value="pending">Pending</option>
                  <option value="invited">Invited</option>
                  <option value="sent">Sent</option>
                  <option value="completed">Completed</option>
                </select>
              </div>
            </div>

            {/* Date Filter */}
            <div className="md:col-span-2 lg:col-span-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">Date Range</label>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:space-x-2">
                <div className="relative flex-1 min-w-0">
                  <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none z-10" />
                  <input
                    type="date"
                    value={candidateDateFilter.start}
                    onChange={(e) => setCandidateDateFilter({ ...candidateDateFilter, start: e.target.value })}
                    className="w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    placeholder="Start date"
                  />
                </div>
                <span className="text-gray-500 text-sm self-center sm:self-auto">to</span>
                <div className="relative flex-1 min-w-0">
                  <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none z-10" />
                  <input
                    type="date"
                    value={candidateDateFilter.end}
                    onChange={(e) => setCandidateDateFilter({ ...candidateDateFilter, end: e.target.value })}
                    className="w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    placeholder="End date"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Results Count */}
        <div className="mb-4 flex items-center justify-between">
          <div className="text-sm text-gray-600">
            {candidatesTotal > 0 ? (
              <>Showing {((currentPage - 1) * 20 + 1)} to {Math.min(currentPage * 20, candidatesTotal)} of {candidatesTotal} candidates</>
            ) : (
              <>No candidates found</>
            )}
          </div>
          {candidatesLoading && (
            <div className="text-sm text-blue-600 flex items-center space-x-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
              <span>Loading...</span>
            </div>
          )}
        </div>

        {/* Candidates Table */}
        {allCandidates.length > 0 ? (
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <th className="text-left py-4 px-6 text-sm font-semibold text-gray-700">First Name</th>
                    <th className="text-left py-4 px-6 text-sm font-semibold text-gray-700">Last Name</th>
                    <th className="text-left py-4 px-6 text-sm font-semibold text-gray-700">Email</th>
                    <th className="text-left py-4 px-6 text-sm font-semibold text-gray-700">Phone</th>
                    <th className="text-left py-4 px-6 text-sm font-semibold text-gray-700">Assessment</th>
                    <th className="text-left py-4 px-6 text-sm font-semibold text-gray-700">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {allCandidates.map((candidate, index) => (
                    <tr key={candidate._id || index} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                      <td className="py-4 px-6 text-sm text-gray-900">{candidate.firstName || '-'}</td>
                      <td className="py-4 px-6 text-sm text-gray-900">{candidate.lastName || '-'}</td>
                      <td className="py-4 px-6 text-sm text-gray-900">{candidate.email || '-'}</td>
                      <td className="py-4 px-6 text-sm text-gray-900">{candidate.phone || '-'}</td>
                      <td className="py-4 px-6 text-sm text-gray-900">
                      {candidate?.assessmentId && candidate.sessionStatus === 'completed' ? (
                          <Link to={`/details?sessionId=${candidate.sessionId}&assessmentId=${candidate.assessmentId}`}>
                            <span className="text-blue-600 hover:text-blue-700">{candidate.assessmentTitle || candidate.assessmentRole || "-"}</span>
                          </Link>
                        ) : (
                          <span>{candidate.assessmentTitle || candidate.assessmentRole || "-"}</span>
                        )}
                       
                      </td>
                      <td className="py-4 px-6">
                        {getSessionStatus(candidate.sessionStatus)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* Pagination Controls */}
            {totalPages > 1 && onPageChange && (
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4 px-6 py-4 border-t border-gray-200 bg-gray-50">
                <div className="text-sm text-gray-600">
                  {candidatesTotal > 0 ? (
                    <>Showing {((currentPage - 1) * 20 + 1)} to {Math.min(currentPage * 20, candidatesTotal)} of {candidatesTotal} candidates</>
                  ) : (
                    <>No candidates found</>
                  )}
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => onPageChange(currentPage - 1)}
                    disabled={currentPage === 1 || candidatesLoading}
                    className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-1 transition-colors"
                  >
                    <ChevronLeft className="h-4 w-4" />
                    <span>Previous</span>
                  </button>
                  
                  <div className="flex items-center space-x-1">
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum: number;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }
                      
                      return (
                        <button
                          key={pageNum}
                          onClick={() => onPageChange(pageNum)}
                          disabled={candidatesLoading}
                          className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                            currentPage === pageNum
                              ? 'bg-blue-600 text-white'
                              : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                          } disabled:opacity-50 disabled:cursor-not-allowed`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>
                  
                  <button
                    onClick={() => onPageChange(currentPage + 1)}
                    disabled={currentPage === totalPages || candidatesLoading}
                    className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-1 transition-colors"
                  >
                    <span>Next</span>
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : !candidatesLoading && allCandidates.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
            <Users className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No Candidates</h3>
            <p className="text-gray-600">
              No candidates have been added yet. Invite candidates from an assessment to get started.
            </p>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default AllCandidatesList;
